// "Integrations" tab (project detail, plan §2 WS5): connect a project to a
// Plane project, then map its Plane states/labels onto Goblic's workflow
// states/workflow keys. Goblic is always the source of truth for a task's
// workflow state (project docstring, plan intro) — this tab only configures
// how that state mirrors to/from Plane: an inbound webhook (new work item ->
// task, work item state change -> `workflowEngine.transition(...,
// { kind: 'external' })`) and an outbound write-back after a run (flow
// engine, via the same `stateMapping`). See routes/webhooks/plane.ts and
// services/plane.service.ts.
//
// Never round-trips `apiToken`/`webhookSecret` — the status the API returns
// only ever carries `hasApiToken`/`hasWebhookSecret` booleans (FD §19.1);
// this form only ever sends a new value, never reads one back.

import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Project } from '@goblic/shared'
import {
  ApiError,
  planeIntegrationApi,
  type PlaneIntegrationStatus,
  type PlaneIntegrationTestResult,
  type PlaneTriggerCondition,
  type UpsertPlaneIntegrationBody,
} from '../../lib/api-client'
import { EmptyState, ErrorNote, Field, LoadingRows, Notice, Panel } from '../ui'

const CONDITION_FIELD_LABEL: Record<PlaneTriggerCondition['field'], string> = {
  project: 'Project',
  state: 'State',
  label: 'Label',
  cycle: 'Cycle',
  approved_spec: 'Approved spec (not available yet)',
}

function defaultConditionFor(
  field: PlaneTriggerCondition['field']
): PlaneTriggerCondition {
  switch (field) {
    case 'project':
      return { field: 'project', op: 'equals', value: '' }
    case 'state':
      return { field: 'state', op: 'equals', value: '' }
    case 'label':
      return { field: 'label', op: 'contains', value: '' }
    case 'cycle':
      return { field: 'cycle', op: 'is_set' }
    case 'approved_spec':
      return { field: 'approved_spec', op: 'exists' }
  }
}

/** Every workflow state name across the project's workflows, in workflow-then-definition order, deduplicated. */
function goblicStateNames(project: Project): string[] {
  const seen = new Set<string>()
  const names: string[] = []
  for (const workflow of project.policy.workflows) {
    for (const state of workflow.states) {
      if (!seen.has(state.name)) {
        seen.add(state.name)
        names.push(state.name)
      }
    }
  }
  return names
}

export function PlaneIntegrationTab({ project }: { project: Project }) {
  const queryClient = useQueryClient()
  const queryKey = ['project', project.id, 'integrations', 'plane']

  const statusQuery = useQuery({
    queryKey,
    queryFn: () => planeIntegrationApi.get(project.id),
  })

  const [testResult, setTestResult] = useState<PlaneIntegrationTestResult | null>(null)

  const invalidate = () => void queryClient.invalidateQueries({ queryKey })

  const testMutation = useMutation({
    mutationFn: () => planeIntegrationApi.test(project.id),
    onSuccess: setTestResult,
  })

  const removeMutation = useMutation({
    mutationFn: () => planeIntegrationApi.remove(project.id),
    onSuccess: () => {
      setTestResult(null)
      invalidate()
    },
  })

  if (statusQuery.isLoading) {
    return (
      <Panel title="Plane">
        <LoadingRows label="Loading the Plane integration…" />
      </Panel>
    )
  }
  if (statusQuery.isError) {
    return (
      <Panel title="Plane">
        <ErrorNote error={statusQuery.error} what="the Plane integration" />
      </Panel>
    )
  }

  const status = statusQuery.data as PlaneIntegrationStatus

  return (
    <div className="space-y-6">
      <PlaneConnectionForm
        project={project}
        status={status}
        onSaved={() => {
          setTestResult(null)
          invalidate()
        }}
      />

      {status.configured ? (
        <>
          <Panel
            title="Webhook"
            description="Add this URL as a webhook on the Plane project so new and updated work items reach Goblic."
          >
            {status.webhookUrl ? (
              <code className="block truncate rounded-control bg-sunken/60 px-3 py-2 font-mono text-sm text-ink">
                {status.webhookUrl}
              </code>
            ) : (
              <Notice tone="signal">
                Set <code className="font-mono">GOBLIC_PUBLIC_API_URL</code> on
                the API so a webhook URL can be shown here.
              </Notice>
            )}
          </Panel>

          <Panel
            title="Test connection"
            description="Reads the project's states and labels back from Plane with the stored credential."
            actions={
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => testMutation.mutate()}
                disabled={testMutation.isPending}
              >
                {testMutation.isPending ? 'Testing…' : 'Test connection'}
              </button>
            }
          >
            {testResult ? (
              testResult.ok ? (
                <Notice tone="success">
                  Connected — found {testResult.planeStates?.length ?? 0} states
                  and {testResult.planeLabels?.length ?? 0} labels.
                </Notice>
              ) : (
                <Notice tone="error">
                  {testResult.error === 'not_configured'
                    ? 'This project has no Plane connection yet.'
                    : testResult.error === 'credential_unavailable'
                      ? 'The stored credential could not be read.'
                      : (testResult.error ?? 'The test failed.')}
                </Notice>
              )
            ) : (
              <p className="text-base text-ink-3">Not tested yet.</p>
            )}
          </Panel>

          <MappingEditor
            project={project}
            status={status}
            planeStates={testResult?.ok ? (testResult.planeStates ?? []) : []}
            planeLabels={testResult?.ok ? (testResult.planeLabels ?? []) : []}
            onSaved={invalidate}
          />

          <Panel title="Disconnect">
            <p className="mb-3 text-base text-ink-3">
              Stops mirroring this project's Plane work items. The stored
              credential is revoked.
            </p>
            <button
              type="button"
              className="btn-danger btn-sm"
              onClick={() => removeMutation.mutate()}
              disabled={removeMutation.isPending}
            >
              {removeMutation.isPending ? 'Disconnecting…' : 'Disconnect Plane'}
            </button>
          </Panel>
        </>
      ) : null}
    </div>
  )
}

function PlaneConnectionForm({
  project,
  status,
  onSaved,
}: {
  project: Project
  status: PlaneIntegrationStatus
  onSaved: () => void
}) {
  const [baseUrl, setBaseUrl] = useState(status.baseUrl ?? '')
  const [workspaceSlug, setWorkspaceSlug] = useState(status.workspaceSlug ?? '')
  const [planeProjectId, setPlaneProjectId] = useState(status.planeProjectId ?? '')
  const [planeProjectName, setPlaneProjectName] = useState(status.planeProjectName ?? '')
  const [apiToken, setApiToken] = useState('')
  const [webhookSecret, setWebhookSecret] = useState('')
  const [conditions, setConditions] = useState<PlaneTriggerCondition[]>(
    status.triggerConfig?.conditions ?? [defaultConditionFor('label')]
  )
  const [error, setError] = useState<string | null>(null)

  const saveMutation = useMutation({
    mutationFn: (body: UpsertPlaneIntegrationBody) =>
      planeIntegrationApi.update(project.id, body),
    onSuccess: () => {
      setApiToken('')
      setWebhookSecret('')
      setError(null)
      onSaved()
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : String(err)),
  })

  function updateCondition(index: number, patch: Partial<PlaneTriggerCondition>) {
    setConditions((prev) =>
      prev.map((c, i) => (i === index ? ({ ...c, ...patch } as PlaneTriggerCondition) : c))
    )
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (!status.hasApiToken && (!apiToken || !webhookSecret)) {
      setError('An API token and webhook secret are both required to connect.')
      return
    }
    saveMutation.mutate({
      baseUrl,
      workspaceSlug,
      planeProjectId,
      planeProjectName: planeProjectName || null,
      triggerConfig: { conditions },
      stateMapping: status.stateMapping,
      typeLabels: status.typeLabels,
      ...(apiToken ? { apiToken } : {}),
      ...(webhookSecret ? { webhookSecret } : {}),
    })
  }

  return (
    <Panel
      title="Plane connection"
      description={
        status.configured
          ? 'This project mirrors its workflow into a Plane project.'
          : 'Connect this project to a self-hosted Plane project.'
      }
      flush
    >
      <form onSubmit={handleSubmit} className="space-y-4 p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Plane base URL" htmlFor="plane-base-url">
            <input
              id="plane-base-url"
              className="field-input"
              placeholder="https://plane.example.com/api/v1"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              required
            />
          </Field>
          <Field label="Workspace slug" htmlFor="plane-workspace-slug">
            <input
              id="plane-workspace-slug"
              className="field-input"
              value={workspaceSlug}
              onChange={(e) => setWorkspaceSlug(e.target.value)}
              required
            />
          </Field>
          <Field label="Plane project ID" htmlFor="plane-project-id">
            <input
              id="plane-project-id"
              className="field-input"
              value={planeProjectId}
              onChange={(e) => setPlaneProjectId(e.target.value)}
              required
            />
          </Field>
          <Field
            label="Plane project name"
            htmlFor="plane-project-name"
            hint="Optional, shown only in Goblic."
          >
            <input
              id="plane-project-name"
              className="field-input"
              value={planeProjectName}
              onChange={(e) => setPlaneProjectName(e.target.value)}
            />
          </Field>
          <Field
            label="API token"
            htmlFor="plane-api-token"
            hint={
              status.hasApiToken
                ? 'A token is already stored — leave blank to keep it.'
                : 'A Plane workspace API token (X-API-Key).'
            }
          >
            <input
              id="plane-api-token"
              className="field-input"
              type="password"
              autoComplete="off"
              placeholder={status.hasApiToken ? '••••••••' : ''}
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
            />
          </Field>
          <Field
            label="Webhook secret"
            htmlFor="plane-webhook-secret"
            hint={
              status.hasWebhookSecret
                ? 'Already stored — leave blank to keep it.'
                : 'Shared secret Plane signs its webhook deliveries with.'
            }
          >
            <input
              id="plane-webhook-secret"
              className="field-input"
              type="password"
              autoComplete="off"
              placeholder={status.hasWebhookSecret ? '••••••••' : ''}
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
            />
          </Field>
        </div>

        <fieldset className="space-y-2">
          <legend className="mb-1 text-base font-medium text-ink">
            Trigger conditions
          </legend>
          <p className="text-sm text-ink-3">
            A new work item only becomes a task when every condition below
            holds.
          </p>
          <ul className="space-y-2">
            {conditions.map((condition, index) => (
              <li key={index} className="flex flex-wrap items-center gap-2">
                <select
                  className="field-input w-auto"
                  value={condition.field}
                  onChange={(e) =>
                    setConditions((prev) =>
                      prev.map((c, i) =>
                        i === index
                          ? defaultConditionFor(
                              e.target.value as PlaneTriggerCondition['field']
                            )
                          : c
                      )
                    )
                  }
                >
                  {Object.entries(CONDITION_FIELD_LABEL).map(([value, label]) => (
                    <option
                      key={value}
                      value={value}
                      // Not implemented server-side (always fails): an existing
                      // row keeps showing it, but it can't be newly picked.
                      disabled={value === 'approved_spec' && condition.field !== 'approved_spec'}
                    >
                      {label}
                    </option>
                  ))}
                </select>
                {condition.field === 'label' ? (
                  <select
                    className="field-input w-auto"
                    value={condition.op}
                    onChange={(e) =>
                      updateCondition(index, {
                        op: e.target.value as 'contains' | 'not_contains',
                      })
                    }
                  >
                    <option value="contains">contains</option>
                    <option value="not_contains">doesn’t contain</option>
                  </select>
                ) : condition.field === 'cycle' ? (
                  <select
                    className="field-input w-auto"
                    value={condition.op}
                    onChange={(e) =>
                      updateCondition(index, {
                        op: e.target.value as 'is_set' | 'is_not_set',
                      })
                    }
                  >
                    <option value="is_set">is set</option>
                    <option value="is_not_set">is not set</option>
                  </select>
                ) : null}
                {'value' in condition ? (
                  <input
                    className="field-input w-auto flex-1"
                    placeholder="value"
                    value={condition.value}
                    onChange={(e) => updateCondition(index, { value: e.target.value } as Partial<PlaneTriggerCondition>)}
                  />
                ) : null}
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() =>
                    setConditions((prev) => prev.filter((_, i) => i !== index))
                  }
                  disabled={conditions.length <= 1}
                >
                  Remove
                </button>
                {condition.field === 'approved_spec' ? (
                  <p className="basis-full text-sm text-signal">
                    This condition isn’t supported yet and always fails, so no
                    task will be created. Remove it or use a state/label
                    condition.
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() =>
              setConditions((prev) => [...prev, defaultConditionFor('label')])
            }
          >
            Add condition
          </button>
        </fieldset>

        {error ? <Notice tone="error">{error}</Notice> : null}
        {saveMutation.isError && !error ? (
          <Notice tone="error">
            {saveMutation.error instanceof ApiError
              ? saveMutation.error.message
              : 'Could not save the Plane connection.'}
          </Notice>
        ) : null}

        <div className="flex justify-end">
          <button
            type="submit"
            className="btn-primary"
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending
              ? 'Saving…'
              : status.configured
                ? 'Save connection'
                : 'Connect Plane'}
          </button>
        </div>
      </form>
    </Panel>
  )
}

function MappingEditor({
  project,
  status,
  planeStates,
  planeLabels,
  onSaved,
}: {
  project: Project
  status: PlaneIntegrationStatus
  planeStates: string[]
  planeLabels: string[]
  onSaved: () => void
}) {
  const [stateMapping, setStateMapping] = useState<Record<string, string>>(
    status.stateMapping
  )
  const [labelRows, setLabelRows] = useState<{ label: string; workflowKey: string }[]>(
    Object.entries(status.typeLabels).map(([label, workflowKey]) => ({
      label,
      workflowKey,
    }))
  )

  const goblicStates = goblicStateNames(project)
  const workflowKeys = project.policy.workflows.map((w) => ({
    key: w.key,
    name: w.name,
  }))

  const saveMutation = useMutation({
    mutationFn: () =>
      planeIntegrationApi.update(project.id, {
        baseUrl: status.baseUrl as string,
        workspaceSlug: status.workspaceSlug as string,
        planeProjectId: status.planeProjectId as string,
        planeProjectName: status.planeProjectName,
        triggerConfig: status.triggerConfig ?? { conditions: [] },
        // A cleared field unmaps the state: leave it out rather than send ''.
        stateMapping: Object.fromEntries(
          Object.entries(stateMapping)
            .map(([name, planeState]) => [name, planeState.trim()])
            .filter(([, planeState]) => planeState)
        ),
        typeLabels: Object.fromEntries(
          labelRows
            .filter((row) => row.label && row.workflowKey)
            .map((row) => [row.label, row.workflowKey])
        ),
      }),
    onSuccess: onSaved,
  })

  if (goblicStates.length === 0 && workflowKeys.length === 0) {
    return (
      <Panel title="Workflow mapping">
        <EmptyState title="No workflows yet" art={null}>
          Add a workflow on the Workflows tab first, then map its states and
          task types here.
        </EmptyState>
      </Panel>
    )
  }

  return (
    <Panel
      title="Workflow mapping"
      description="Which Plane state mirrors each Goblic workflow state, and which task type a Plane label picks."
      actions={
        <button
          type="button"
          className="btn-primary btn-sm"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save mapping'}
        </button>
      }
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <h3 className="mb-2 text-base font-medium text-ink">
            Goblic state → Plane state
          </h3>
          <p className="mb-2 text-sm text-ink-3">
            Plane state names must match exactly (case-sensitive).
          </p>
          {goblicStates.length === 0 ? (
            <p className="text-sm text-ink-3">No workflow states yet.</p>
          ) : (
            <ul className="space-y-2">
              {goblicStates.map((name) => (
                <li key={name} className="flex items-center gap-2">
                  <span className="w-40 shrink-0 truncate text-sm text-ink">
                    {name}
                  </span>
                  <input
                    className="field-input"
                    list={planeStates.length ? 'plane-state-options' : undefined}
                    placeholder="Plane state name"
                    value={stateMapping[name] ?? ''}
                    onChange={(e) =>
                      setStateMapping((prev) => ({ ...prev, [name]: e.target.value }))
                    }
                  />
                </li>
              ))}
            </ul>
          )}
          {planeStates.length ? (
            <datalist id="plane-state-options">
              {planeStates.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          ) : null}
        </div>

        <div>
          <h3 className="mb-2 text-base font-medium text-ink">
            Plane label → task type
          </h3>
          <p className="mb-2 text-sm text-ink-3">
            If a work item has several mapped labels, its first label wins; with
            no matching label the project’s default workflow is used.
          </p>
          <ul className="space-y-2">
            {labelRows.map((row, index) => (
              <li key={index} className="flex items-center gap-2">
                <input
                  className="field-input"
                  list={planeLabels.length ? 'plane-label-options' : undefined}
                  placeholder="Plane label, e.g. type:bug"
                  value={row.label}
                  onChange={(e) =>
                    setLabelRows((prev) =>
                      prev.map((r, i) =>
                        i === index ? { ...r, label: e.target.value } : r
                      )
                    )
                  }
                />
                <select
                  className="field-input w-auto"
                  value={row.workflowKey}
                  onChange={(e) =>
                    setLabelRows((prev) =>
                      prev.map((r, i) =>
                        i === index ? { ...r, workflowKey: e.target.value } : r
                      )
                    )
                  }
                >
                  <option value="">— workflow —</option>
                  {workflowKeys.map((w) => (
                    <option key={w.key} value={w.key}>
                      {w.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() =>
                    setLabelRows((prev) => prev.filter((_, i) => i !== index))
                  }
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
          {planeLabels.length ? (
            <datalist id="plane-label-options">
              {planeLabels.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          ) : null}
          <button
            type="button"
            className="btn-secondary btn-sm mt-2"
            onClick={() =>
              setLabelRows((prev) => [...prev, { label: '', workflowKey: '' }])
            }
          >
            Add label
          </button>
        </div>
      </div>
    </Panel>
  )
}
